import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { COST_UNIT } from "../../Units";
import { normalizeDisplayLabel } from "../../utility/displayText";
import { LegendSummaryTableProps } from "./types";
import { calculateLegendStats } from "./calculateLegendStats";

const HEADER_FILL: ExcelJS.Fill = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFD9E1F2" },
};

const ZEBRA_FILL: ExcelJS.Fill = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFF2F5FB" },
};

const HEADER_FONT: Partial<ExcelJS.Font> = {
  bold: true,
  color: { argb: "FF1F3864" },
};

const HEADER_ALIGNMENT: Partial<ExcelJS.Alignment> = {
  vertical: "middle",
  horizontal: "left",
  wrapText: true,
};

const DATA_ALIGNMENT: Partial<ExcelJS.Alignment> = {
  vertical: "middle",
  horizontal: "left",
};

const NUM_FORMAT = "#,##0.00";
const INT_FORMAT = "#,##0";

function applyHeaderStyle(row: ExcelJS.Row, colCount: number) {
  for (let i = 1; i <= colCount; i++) {
    const cell = row.getCell(i);
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
    cell.alignment = HEADER_ALIGNMENT;
    cell.border = {
      bottom: { style: "thin", color: { argb: "FF1F3864" } },
    };
  }
  row.height = 36;
}

function applyDataRowStyle(
  row: ExcelJS.Row,
  colCount: number,
  rowIndex: number,
  integerCols: Set<number> = new Set(),
) {
  const isOdd = rowIndex % 2 !== 0;
  for (let i = 1; i <= colCount; i++) {
    const cell = row.getCell(i);
    cell.alignment = DATA_ALIGNMENT;
    if (isOdd) cell.fill = ZEBRA_FILL;
    if (typeof cell.value === "number") {
      cell.numFmt = integerCols.has(i) ? INT_FORMAT : NUM_FORMAT;
    }
  }
}

function autoFitColumns(worksheet: ExcelJS.Worksheet, headers: string[]) {
  worksheet.columns.forEach((col, idx) => {
    const header = headers[idx] ?? "";
    const longestWord = header
      .split(/\s+/)
      .reduce((a, b) => (b.length > a.length ? b : a), "");
    let maxLen = Math.max(header.length * 0.6, longestWord.length + 2);
    (col as ExcelJS.Column).eachCell({ includeEmpty: false }, (cell) => {
      const val = cell.value ? String(cell.value) : "";
      if (val.length > maxLen) maxLen = val.length;
    });
    (col as ExcelJS.Column).width = Math.max(14, Math.min(maxLen + 4, 20));
  });
}

function applySheetExtras(ws: ExcelJS.Worksheet, colCount: number) {
  ws.views = [{ state: "frozen", xSplit: 0, ySplit: 1 }];
  ws.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: colCount },
  };
}

function setWorkbookMeta(workbook: ExcelJS.Workbook, title: string) {
  workbook.creator = "GNA Energy";
  workbook.lastModifiedBy = "GNA Energy";
  workbook.created = new Date();
  workbook.modified = new Date();
  workbook.title = title;
}

function timestampedFilename(base: string): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}`;
  return `${base}_${stamp}.xlsx`;
}

function generateWatermarkBase64(): string {
  const canvas = document.createElement("canvas");
  canvas.width = 600;
  canvas.height = 400;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.save();
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(-Math.PI / 4);
  ctx.font = "bold 80px Arial";
  ctx.fillStyle = "rgba(180, 200, 230, 0.1)";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("GNAi", 0, 0);
  ctx.restore();
  return canvas.toDataURL("image/png").split(",")[1];
}

function addWatermark(workbook: ExcelJS.Workbook, ws: ExcelJS.Worksheet): void {
  const base64 = generateWatermarkBase64();
  const imageId = workbook.addImage({ base64, extension: "png" });
  ws.addBackgroundImage(imageId);
}

async function triggerDownload(
  workbook: ExcelJS.Workbook,
  filename: string,
): Promise<void> {
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: "application/octet-stream" }), filename);
}

export async function downloadSummaryCSV({
  legends,
  data,
  unit = "MWh",
  tableData,
  extraKeys,
  extraLabels,
  filename = "summary",
}: LegendSummaryTableProps): Promise<void> {
  const stats = calculateLegendStats(unit, legends, data, extraKeys, tableData);

  const unitLc = String(unit).toLowerCase();
  // _mw-keyed series get energy-converted totals (sum/4 → MWh) in
  // calculateLegendStats; all other units sum raw, so Total carries the same
  // unit as the value columns.
  const POWER_UNITS: Record<string, { value: string; total: string }> = {
    mw: { value: "MW", total: "MWh" },
    mwh: { value: "MWh", total: "MWh" },
    kw: { value: "KW", total: "KW" },
    kwh: { value: "KWh", total: "KWh" },
    lu: { value: "LU", total: "LU" },
  };
  const mapped = POWER_UNITS[unitLc];
  const valueUnitLabel = mapped ? mapped.value : "MUs";
  const totalLabel = mapped ? mapped.total : "MUs";

  const capitalizeFirst = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  const headers = [
    "Name",
    `Min (${valueUnitLabel})`,
    ...extraLabels.map((label) => `Min - ${capitalizeFirst(label)}`),
    `Max (${valueUnitLabel})`,
    ...extraLabels.map((label) => `Max - ${capitalizeFirst(label)}`),
    `Avg (${valueUnitLabel})`,
    `Total (${totalLabel})`,
  ];

  // Summary row layout: Name(1), Min(2), minExtras[0..n](3..2+n), Max(3+n), maxExtras[0..n](4+n..3+2n), Avg, Total
  // Block/slot extra cols land at: idx+3 (min side) and idx+extraKeys.length+4 (max side)
  const integerCols = new Set<number>(
    extraKeys.flatMap((k, idx) =>
      k.toLowerCase() === "block" || k.toLowerCase() === "slot"
        ? [idx + 3, idx + extraKeys.length + 4]
        : [],
    ),
  );

  const workbook = new ExcelJS.Workbook();
  setWorkbookMeta(workbook, `${filename} Summary`);
  const ws = workbook.addWorksheet("Summary");
  addWatermark(workbook, ws);

  ws.addRow(headers);
  applyHeaderStyle(ws.getRow(1), headers.length);
  applySheetExtras(ws, headers.length);

  stats.forEach((stat, i) => {
    const row: (string | number)[] = [
      stat.name,
      stat.min,
      ...extraKeys.map((k) => stat.minExtra[k]),
      stat.max,
      ...extraKeys.map((k) => stat.maxExtra[k]),
      stat.avg,
      stat.total,
    ];
    const addedRow = ws.addRow(row);
    applyDataRowStyle(addedRow, headers.length, i, integerCols);
  });

  autoFitColumns(ws, headers);
  await triggerDownload(
    workbook,
    timestampedFilename(`${filename} Summary Data`),
  );
}

export async function downloadChartTableCSV({
  filename = "chart_table",
  legends,
  data,
  unit,
  extraKeys,
  extraLabels,
}: LegendSummaryTableProps): Promise<void> {
  function calculateLegendHeader(legend: any): string {
    const displayName = normalizeDisplayLabel(legend.name).trim();
    if (/\(forecast\)\s*$/i.test(displayName)) return displayName;

    let suffix = "";
    if (legend?.unit !== undefined) {
      suffix = legend.unit;
    } else {
      const suffixDict: Record<string, string> = {
        Price: COST_UNIT,
        Percent: "%",
        "%": "%",
        Cost: "Rs",
        Revenue: "Rs",
      };
      const foundKey = Object.keys(suffixDict).find((key) =>
        legend.name?.toString().includes(key),
      );
      suffix = foundKey ? suffixDict[foundKey] : legend?.unit || unit || "";
    }

    const trailingParenMatch = displayName.match(/\(([^)]*)\)\s*$/);
    const trailingParenValue = trailingParenMatch?.[1]?.trim().toLowerCase();
    const suffixValue = String(suffix || "")
      .trim()
      .toLowerCase();
    const cleanName =
      trailingParenValue && suffixValue && trailingParenValue === suffixValue
        ? displayName.replace(/\s*\([^)]*\)\s*$/, "").trim()
        : displayName;
    return `${cleanName} (${suffix})`;
  }

  const capitalizeFirst = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  const headers = [
    ...extraKeys.map((key, idx) => capitalizeFirst(extraLabels[idx] || key)),
    ...legends.map((legend) => calculateLegendHeader(legend)),
  ];

  const integerCols = new Set<number>(
    extraKeys
      .map((k, idx) =>
        k.toLowerCase() === "block" || k.toLowerCase() === "slot"
          ? idx + 1
          : -1,
      )
      .filter((i) => i !== -1),
  );

  const formatNum = (v: any): string | number => {
    if (v === undefined || v === null || v === "") return "";
    const n = Number(v);
    return isNaN(n) ? String(v) : Math.round(n * 100) / 100;
  };

  const workbook = new ExcelJS.Workbook();
  setWorkbookMeta(workbook, `${filename} Chart Data`);
  const ws = workbook.addWorksheet("Chart Data");
  addWatermark(workbook, ws);

  ws.addRow(headers);
  applyHeaderStyle(ws.getRow(1), headers.length);
  applySheetExtras(ws, headers.length);

  data.forEach((row, i) => {
    const extraValues = extraKeys.map((key) => row[key] ?? "");
    const legendValues = legends.map((legend) => {
      const raw = row[legend.dataKey];
      if (raw === undefined || raw === null) return "";
      const formatted = formatNum(raw);
      if (legend.deviationValueKey && row[legend.deviationValueKey] != null) {
        return `${formatted} (${formatNum(row[legend.deviationValueKey])})`;
      }
      return formatted;
    });
    const addedRow = ws.addRow([...extraValues, ...legendValues]);
    applyDataRowStyle(addedRow, headers.length, i, integerCols);
  });

  autoFitColumns(ws, headers);
  await triggerDownload(
    workbook,
    timestampedFilename(`${filename} Chart Data`),
  );
}
